import { Decoration } from '../../../../../models/configurators-2d/applications/fence/decoration';
import { distinct } from '../../../../../services/helpers/utils';
import { FencePanelCalculatorService } from '../../services/fence-panel-calculator.service';
import { FencePreviewItem } from '../models/fence-preview-item';
import { FencePreviewItemTypeEnum } from '../models/fence-preview-item-type.enum';
import { FencePreviewProduct } from '../models/fence-preview-product';
import { FencePreviewResumeGroup } from '../models/fence-preview-resume-group';
import { FenceService } from '../../../../../services/configurators-2d/applications/fence.service';
import {
  getMaxHeight,
  getSectionPanelCount,
  getWidthRatio,
  isDecorationEqualOrGreaterThanToPanelCount,
  rotateImg,
  setPanelCount,
} from '../../../../../services/helpers/fence-helper';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Post } from '../../../../../models/configurators-2d/applications/fence/post';
import { Side } from '../../../../../models/configurators-2d/applications/fence/side';
import { FencePanelInstallationColors } from '../../models/fence-panel-installation-colors';
import { Guid } from 'guid-typescript';
import { InstallationTypeEnum } from '../../../../../enum/fence/installation-type.enum';

@Injectable({
  providedIn: 'root',
})
export class FencePreviewFacadeService {
  /** Liste des items permettant de construire la préview de l'étape 3 et 4 */
  items: WritableSignal<FencePreviewItem[]> = signal([]);

  differentHeights: WritableSignal<number[]> = signal([]);

  /**
   * Liste des groupes d'items permettant de construire la préview de l'étape 5
   */
  resumeGroups: WritableSignal<FencePreviewResumeGroup[]> = signal([]);

  verticalPathArray: WritableSignal<any[]> = signal([]);

  private readonly fenceService = inject(FenceService);
  private readonly fencePanelCalculatorService = inject(FencePanelCalculatorService);

  private get configuration() {
    return this.fenceService.configuration;
  }

  /**
   * Est-ce qu'au moins une pose est sélectionnée, toutes les lames affectées aux poses et un type de poteau sélectionné ?
   */
  private get configurationIsComplete(): boolean {
    return (
      this.configuration.configurationInstallations.length > 0 &&
      this.configuration.configurationInstallations.every(i => i.configurationBoards.length > 0) &&
      this.configuration.configurationInstallations.every(i =>
        i.configurationBoards.every(b => b.boardId && b.fenceTypeId)
      ) &&
      !!this.configuration.postType
    );
  }

  /**
   * Fonction de construction de l'objet de prévisualisation
   * En partant de gauche vers la droite, on doit trouver :
   * - Un poteau
   * - Un premier panneau de lames
   * - Un poteau
   * - L'un des portillons sélectionnés
   * - Un poteau
   * - Si un décor horizontal a été sélectionné, un deuxième panneau de lames avec le décor
   * - Un poteau
   * - Si un décor vertical a été sélectionné, le décor vertical
   * - Un poteau
   * - Si un panneau horizontal a été sélectionné, un deuxième panneau de lames avec le panneau
   * - Un poteau
   * - Si un panneau vertical a été sélectionné, le panneau vertical
   * - Un poteau
   * @returns tableau d'items
   */
  async constructPreview(): Promise<void> {
    if (!this.configurationIsComplete) {
      return;
    }

    this.items.set([]);

    const maxHeight = getMaxHeight(this.configuration);

    // Construire le tableau localement pour éviter les détections de changement en boucle
    const items: FencePreviewItem[] = [];

    const nextPosition = () => {
      return items[items.length - 1].position + 1;
    };

    //On insère le premier poteau
    items.push(this.createPost(1));

    //On intègre les lames dans l'objet final
    if (isDecorationEqualOrGreaterThanToPanelCount(this.configuration, 'horizontal')) {
      const allDecorations = [
        ...this.configuration.horizontalDecorativeBoards,
        ...this.configuration.horizontalDecorations,
        ...this.configuration.horizontalPanels,
      ];

      const decorationWithMostElements = this.getDecorationWithMaxQuantity(allDecorations);

      items.push(await this.buildBoards(maxHeight, nextPosition(), [decorationWithMostElements]));
    } else {
      items.push(await this.buildBoards(maxHeight, nextPosition()));
    }

    //On insère le second poteau
    items.push(this.createPost(nextPosition()));

    //Insertion du portillon
    if (this.configuration.doors != null && this.configuration.doors.length > 0) {
      items.push({
        id: Guid.create(),
        position: nextPosition(),
        product: {
          type: FencePreviewItemTypeEnum.Door,
          path: this.fenceService.imgPath(this.configuration.doors[0].path),
        },
      });
      items.push(this.createPost(nextPosition()));
    }

    //Insertion des lames décoratives horizontales
    let horizontalDecorativeBoard = await this.getDecorationPath('decorative-board', 'horizontal');
    if (horizontalDecorativeBoard) {
      //On intègre les lames dans l'objet final
      items.push(
        await this.buildBoards(maxHeight, nextPosition(), [
          this.getDecorationWithMaxQuantity(this.configuration.horizontalDecorativeBoards),
        ])
      );
    } else {
      items.push(await this.buildBoards(maxHeight, nextPosition()));
    }

    items.push(this.createPost(nextPosition()));

    //Insertion des lames décoratives verticales
    let verticalDecorativeBoard = await this.getDecorationPath('decorative-board', 'vertical');
    if (verticalDecorativeBoard) {
      //Si un panneau vertical a été choisi, on l'insère
      items.push({
        id: Guid.create(),
        position: nextPosition(),
        product: {
          type: FencePreviewItemTypeEnum.VerticalDecorativeBoard,
          path: verticalDecorativeBoard,
        },
      });
      items.push(this.createPost(nextPosition()));
    }

    //Insertion des décors horizontaux
    let horizontalDecoration = await this.getDecorationPath('decoration', 'horizontal');
    if (horizontalDecoration) {
      //On intègre les lames dans l'objet final
      items.push(
        await this.buildBoards(maxHeight, nextPosition(), [
          this.getDecorationWithMaxQuantity(this.configuration.horizontalDecorations),
        ])
      );

      //On insère le poteau
      items.push(this.createPost(nextPosition()));
    }

    //Insertion des décors verticaux
    let verticalDecoration = await this.getDecorationPath('decoration', 'vertical');

    if (verticalDecoration) {
      //Si un décor vertical a été choisi, on l'insère
      items.push({
        id: Guid.create(),
        position: nextPosition(),
        product: {
          type: FencePreviewItemTypeEnum.VerticalDecoration,
          path: verticalDecoration,
        },
      });
      items.push(this.createPost(nextPosition()));
    }

    //Insertion des panneaux horizontaux
    let horizontalPanel = await this.getDecorationPath('panel', 'horizontal');
    if (horizontalPanel) {
      //On intègre les lames dans l'objet final
      items.push(
        await this.buildBoards(maxHeight, nextPosition(), [
          this.getDecorationWithMaxQuantity(this.configuration.horizontalPanels),
        ])
      );

      //On insère le poteau
      items.push(this.createPost(nextPosition()));
    }

    //Insertion des panneaux verticaux
    let verticalPanel = await this.getDecorationPath('panel', 'vertical');
    if (verticalPanel) {
      //Si un panneau vertical a été choisi, on l'insère
      items.push({
        id: Guid.create(),
        position: nextPosition(),
        product: {
          type: FencePreviewItemTypeEnum.VerticalPanel,
          path: verticalPanel,
        },
      });
      items.push(this.createPost(nextPosition()));
    }

    // Assigner une seule fois à la fin pour éviter la détection de changement en boucle
    const filteredItems = items.filter(i => i != null);

    // Utiliser setTimeout pour forcer l'assignation dans le prochain tick et éviter les doubles détections
    await new Promise(resolve => setTimeout(resolve, 0));

    // Créer un nouveau tableau pour forcer la détection de changement
    this.items.set([...filteredItems]);
  }

  /**
   * Construction des groupes à afficher dans le récap
   * @private
   */
  async constructResumeGroups(): Promise<void> {
    if (!this.configurationIsComplete) {
      return;
    }

    this.resumeGroups.set([]);

    let configuration = this.configuration;
    let position = 0;
    let nextPosition = function () {
      position++;
      return position;
    };
    const maxHeight = getMaxHeight(configuration);

    let groups: FencePreviewResumeGroup[] = [];

    setPanelCount(configuration);
    let wallCount = configuration.panelCount.completeCount;

    // lames décoratives
    // mur avec des lames décoratives verticales
    if (configuration.sides.some(s => s.verticalDecorativeBoards != null)) {
      //on crée un tableau distinctDeco avec tous les verticalDecorations distincts
      let allDecoBoards = configuration.sides
        .map(side => side.verticalDecorativeBoards)
        .reduce((acc, val) => acc.concat(val), [])
        .filter(d => d.quantity > 0);

      const distinctDecoBoard = allDecoBoards.reduce((acc: Decoration[], current: Decoration) => {
        const existDecoBoard = acc.find(d => d.id === current.id);
        if (existDecoBoard) {
          existDecoBoard.quantity += current.quantity;
        } else {
          acc.push({ ...current });
        }
        return acc;
      }, []);

      for (const decoBoard of distinctDecoBoard) {
        groups.push({
          id: Guid.create(),
          items: [
            this.createPost(nextPosition()),
            {
              id: Guid.create(),
              position: nextPosition(),
              product: {
                type: FencePreviewItemTypeEnum.VerticalDecorativeBoard,
                path: decoBoard.verticalPath,
              },
            },
            this.createPost(nextPosition()),
          ],
          quantity: decoBoard.quantity,
          type: FencePreviewItemTypeEnum.VerticalDecorativeBoard,
        });
      }
    }

    //murs avec des lames décoratives horizontales
    if (configuration.horizontalDecorativeBoards != null) {
      for (let i = 0; i < configuration.horizontalDecorativeBoards.length; i++) {
        let decorativeBoard = configuration.horizontalDecorativeBoards[i];

        if (decorativeBoard.quantity > 0) {
          wallCount -= decorativeBoard.quantity;

          groups.push({
            id: Guid.create(),
            items: [
              this.createPost(nextPosition()),
              await this.buildBoards(maxHeight, nextPosition(), [decorativeBoard]),
              this.createPost(nextPosition()),
            ],
            quantity: decorativeBoard.quantity,
            type: FencePreviewItemTypeEnum.HorizontalDecorativeBoard,
          });
        }
      }
    }

    // décorations
    // mur avec des décorations verticales
    if (configuration.sides.some(s => s.verticalDecorations != null)) {
      //on crée un tableau distinctDeco avec tous les verticalDecorations distincts
      let alldeco = configuration.sides
        .map(side => side.verticalDecorations)
        .reduce((acc, val) => acc.concat(val), [])
        .filter(d => d.quantity > 0);

      const distinctDeco = alldeco.reduce((acc: Decoration[], current: Decoration) => {
        const existDeco = acc.find(d => d.id === current.id);
        if (existDeco) {
          existDeco.quantity += current.quantity;
        } else {
          acc.push({ ...current });
        }
        return acc;
      }, []);

      for (const deco of distinctDeco) {
        groups.push({
          id: Guid.create(),
          items: [
            this.createPost(nextPosition()),
            {
              id: Guid.create(),
              position: nextPosition(),
              product: {
                type: FencePreviewItemTypeEnum.VerticalDecoration,
                path: deco.verticalPath,
              },
            },
            this.createPost(nextPosition()),
          ],
          quantity: deco.quantity,
          type: FencePreviewItemTypeEnum.VerticalDecoration,
        });
      }
    }

    //murs avec des décorations horizontales
    if (configuration.horizontalDecorations != null) {
      for (let i = 0; i < configuration.horizontalDecorations.length; i++) {
        let decoration = configuration.horizontalDecorations[i];

        if (decoration.quantity > 0) {
          wallCount -= decoration.quantity;

          groups.push({
            id: Guid.create(),
            items: [
              this.createPost(nextPosition()),
              await this.buildBoards(maxHeight, nextPosition(), [decoration]),
              this.createPost(nextPosition()),
            ],
            quantity: decoration.quantity,
            type: FencePreviewItemTypeEnum.HorizontalDecoration,
          });
        }
      }
    }

    // Panneaux
    // mur avec des panneaux verticaux
    if (configuration.sides.some(s => s.verticalPanels != null)) {
      //on crée un tableau distinctPanel avec tous les verticalPanels distincts
      let allPanel = configuration.sides
        .map(side => side.verticalPanels)
        .reduce((acc, val) => acc.concat(val), [])
        .filter(d => d.quantity > 0);

      const distinctPanel = allPanel.reduce((acc: Decoration[], current: Decoration) => {
        const existPanel = acc.find(d => d.id === current.id);
        if (existPanel) {
          existPanel.quantity += current.quantity;
        } else {
          acc.push({ ...current });
        }
        return acc;
      }, []);

      for (const panel of distinctPanel) {
        groups.push({
          id: Guid.create(),
          items: [
            this.createPost(nextPosition()),
            {
              id: Guid.create(),
              position: nextPosition(),
              product: {
                type: FencePreviewItemTypeEnum.VerticalDecoration,
                path: panel.verticalPath,
              },
            },
            this.createPost(nextPosition()),
          ],
          quantity: panel.quantity,
          type: FencePreviewItemTypeEnum.VerticalDecoration,
        });
      }
    }

    // mur avec des panneaux horizontaux
    if (configuration.horizontalPanels != null) {
      for (let i = 0; i < configuration.horizontalPanels.length; i++) {
        let panel = configuration.horizontalPanels[i];
        // murs avec des panneaux horizontaux
        if (panel.quantity > 0) {
          wallCount -= panel.quantity;

          groups.push({
            id: Guid.create(),
            items: [
              this.createPost(nextPosition()),
              await this.buildBoards(maxHeight, nextPosition(), [panel]),
              this.createPost(nextPosition()),
            ],
            quantity: panel.quantity,
            type: FencePreviewItemTypeEnum.HorizontalPanel,
          });
        }
      }
    }

    // portillons
    if (configuration.doors) {
      for (let i = 0; i < configuration.doors.length; i++) {
        let door = configuration.doors[i];
        groups.push({
          id: Guid.create(),
          items: [
            {
              id: Guid.create(),
              position: nextPosition(),
              product: {
                type: FencePreviewItemTypeEnum.Door,
                path: this.fenceService.imgPath(door.path),
                items: undefined,
              },
            },
          ],
          quantity: door.quantity,
          type: FencePreviewItemTypeEnum.Door,
        });
      }
    }

    // murs sans décoration, ni panneau
    if (wallCount > 0) {
      groups.push({
        id: Guid.create(),
        items: [
          this.createPost(nextPosition()),
          await this.buildBoards(maxHeight, nextPosition()),
          this.createPost(nextPosition()),
        ],
        quantity: wallCount,
        type: FencePreviewItemTypeEnum.Board,
      });
    }

    // murs avec des lames à couper
    if (configuration.panelCount.remainingCount) {
      groups.push({
        id: Guid.create(),
        items: [
          this.createPost(nextPosition()),
          await this.buildBoards(
            maxHeight,
            nextPosition(),
            [],
            true,
            configuration.panelCount.remainingVerticalBoardsCount
          ),
          this.createPost(nextPosition()),
        ],
        quantity: configuration.panelCount.remainingCount,
        type: FencePreviewItemTypeEnum.BoardCut,
        isVertical: configuration.panelCount.remainingVerticalBoardsCount > 0,
      });
    }

    // ajout du nombre de plaque de soubassement à la preview
    let basePlateMessages: string[] = [];
    let basePlateTotalQuantity = 0;

    for (const side of this.configuration.sides) {
      let quantity = 0;

      for (const section of side.sections.filter(s => s.hasBasePlates)) {
        const sectionPanelCount = getSectionPanelCount(section, side, this.configuration);
        quantity += sectionPanelCount.completeCount + sectionPanelCount.remainingCount;
      }

      basePlateTotalQuantity += quantity;
      basePlateMessages.push(`x${quantity} pour le côté ${side.id}`);
    }

    if (basePlateMessages.length > 0 && basePlateTotalQuantity > 0) {
      groups.push({
        id: Guid.create(),
        items: [
          {
            id: Guid.create(),
            position: 1,
            product: {
              type: FencePreviewItemTypeEnum.BasePlate,
              path: 'assets/img/fence/base-plate-preview.jpg',
            },
          },
        ],
        messages: basePlateMessages,
        type: FencePreviewItemTypeEnum.BasePlate,
      });
    }

    this.resumeGroups.set([...groups]);
  }

  setDifferentHeights(): void {
    const configuration = this.configuration;
    this.differentHeights.set(
      distinct(configuration.sides.reduce((h: number[], s: Side) => h.concat(s.sections.map(ss => ss.height)), []))
    );
  }

  private createPost(position: number): FencePreviewItem {
    const postType = this.configuration.postType;

    if (!postType) {
      return null;
    }

    let product: FencePreviewProduct;
    let maxHeight = getMaxHeight(this.configuration);

    // sélection d'un bon poteau par rapport à la hauteur
    let post: Post;
    let posts: Post[] = postType.posts ?? [];

    //Si poteau à sceller, on prend le poteau étant le plus proche et plus grand que la hauteur max de la clôture
    if (this.configuration.location.isSealed && !postType.isCuttable) {
      posts = posts.filter(p => p.height > maxHeight);
      posts.sort((a, b) => (a.height > b.height ? 1 : a.height < b.height ? -1 : 0));
    } else if (postType.isCuttable) {
      posts = posts.filter(p => p.heightCut == maxHeight);
      posts.sort((a, b) => (a.heightCut < b.heightCut ? 1 : a.heightCut > b.heightCut ? -1 : 0));
    } else {
      posts = posts.filter(p => p.height <= maxHeight);
      posts.sort((a, b) => (a.height < b.height ? 1 : a.height > b.height ? -1 : 0));
    }

    if (posts.length > 0) {
      post = posts[0];
    }

    if (this.configuration.location.isSealed) {
      product = {
        type: FencePreviewItemTypeEnum.SealedPost,
        path: post != undefined ? this.fenceService.imgPath(post.sealedPath) : '',
        isVertical: this.configuration.isVertical,
      };
    } else {
      product = {
        type: FencePreviewItemTypeEnum.PostWithPlate,
        path: post != undefined ? this.fenceService.imgPath(post.path) : '',
        isVertical: this.configuration.isVertical,
      };
    }

    return {
      id: Guid.create(),
      position: position,
      product: product,
    };
  }

  private async buildBoards(
    height: number,
    position: number,
    decorations?: Decoration[],
    cut = false,
    remainingVerticalBoardsCount?: number
  ): Promise<FencePreviewItem> {
    const boardItems: FencePreviewItem[] = [];

    const decoration = decorations?.[decorations?.length - 1];
    const fenceSection = this.fencePanelCalculatorService.calculate(
      height,
      decoration,
      this.configuration.configurationInstallations
    );

    //Si un décor horizontal a été sélectionné, on l'insère avant les lames
    if (fenceSection.horizontalDecoration) {
      boardItems.push(
        this.createItem(
          FencePreviewItemTypeEnum.HorizontalDecoration,
          this.fenceService.imgPath(fenceSection.horizontalDecoration.path),
          boardItems.length + 1
        )
      );
    }

    // on insère une entretoise de fin, si la dernière pose en contient une et si c'est une pose ajourée
    const lastInstallationColor = fenceSection.installationColors[0];
    const lastInstallation = lastInstallationColor.installation;
    const lastColor = lastInstallationColor.colors[0];
    if (lastColor?.finishingSpacerHeight && lastInstallation.installationType === InstallationTypeEnum.Openwork) {
      boardItems.push(this.createSpacerPreviewItem(lastColor.finishingSpacerHeight, boardItems.length + 1));
    }

    for (const installationColor of fenceSection.installationColors) {
      const index = fenceSection.installationColors.indexOf(installationColor);

      // Ajout des lames et les entretoises pour la pose
      if (this.configuration.isVertical) {
        boardItems.push(
          ...(await this.buildVerticalBoardsByInstallation(
            installationColor,
            cut,
            height,
            remainingVerticalBoardsCount
          ))
        );
      } else {
        boardItems.push(...(await this.buildHorizontalBoardsByInstallation(installationColor, cut)));
      }

      // Ajout de l'entretoise visible entre deux poses
      if (index < fenceSection.installationColors.length - 1 && fenceSection.spacerHeightBetweenInstallations > 0) {
        boardItems.push(
          this.createSpacerPreviewItem(fenceSection.spacerHeightBetweenInstallations, boardItems.length + 1)
        );
      }
    }

    // on insère une entretoise de début, si la première pose en contient une et qu'il s'agit d'une pose ajourée
    const firstInstallationColor = fenceSection.installationColors[fenceSection.installationColors.length - 1];
    const firstInstallation = firstInstallationColor.installation;
    const firstColor = firstInstallationColor.colors[firstInstallationColor.colors.length - 1];
    if (firstColor.startingSpacerHeight && firstInstallation.installationType === InstallationTypeEnum.Openwork) {
      boardItems.push(this.createSpacerPreviewItem(firstColor.startingSpacerHeight, boardItems.length + 1));
    }

    return {
      id: Guid.create(),
      position: position,
      product: {
        type: FencePreviewItemTypeEnum.Board,
        items: boardItems,
        isVertical: this.configuration.isVertical,
        isPartial: cut && remainingVerticalBoardsCount > 0,
      },
    };
  }

  private async saveVerticalPathArray(
    widthRatio: number,
    installationColor: FencePanelInstallationColors
  ): Promise<void> {
    if (!this.configuration.isVertical) {
      return;
    }

    const uniqueByPath = installationColor.colors.filter(
      (item, index, self) => index === self.findIndex(x => x.path === item.path)
    );
    const newUniqueByPathAndRatio = uniqueByPath.filter(
      x => !this.verticalPathArray().some(y => y.path === x.path && y.widthRatio === widthRatio)
    );

    if (newUniqueByPathAndRatio.length <= 0) {
      return;
    }

    const newVerticalPathArray = await Promise.all(
      newUniqueByPathAndRatio.map(async x => ({
        path: x.path,
        widthRatio: widthRatio,
        verticalPath: await rotateImg(this.fenceService.imgPath(x.path), 270, widthRatio),
      }))
    );
    this.verticalPathArray.update(values => [...values, ...newVerticalPathArray]);
  }

  private async buildVerticalBoardsByInstallation(
    installationColor: FencePanelInstallationColors,
    cut: boolean,
    height: number,
    remainingVerticalBoardsCount?: number
  ): Promise<FencePreviewItem[]> {
    const boardItems: FencePreviewItem[] = [];

    const color = installationColor.colors[0];
    const profile = color?.verticalProfile;

    // On insère le profilé du haut si on a une image
    if (profile?.pathTop) {
      const profileVisual = this.fenceService.imgPath(profile.pathTop, cut);

      if (profileVisual) {
        boardItems.push(this.createItem(FencePreviewItemTypeEnum.TopProfile, profileVisual, boardItems.length + 1));
      }
    }

    // on charge les images avant de boucler (une seule fois par path) [perf]
    const widthRatio = getWidthRatio(height);
    await this.saveVerticalPathArray(widthRatio, installationColor);

    let maxBoards = installationColor.colors.length;
    if (this.configuration.isVertical && cut && remainingVerticalBoardsCount > 0) {
      maxBoards /= 2; // on affiche la moitié des lames seulement pour la pose verticale d'une clôture partielle
    }

    // on insère les lames
    for (let i = 0; i < maxBoards; i++) {
      const color = installationColor.colors[i];

      // ajout de l'entretoise si la pose en contient et si ce n'est pas la première lame
      // on affiche les entretoises qu'entre deux lames d'une même pose
      if (i > 0 && color.spacerHeight) {
        boardItems.push(this.createSpacerPreviewItem(color.spacerHeight, boardItems.length + 1));
      }

      // Lames horizontales
      if (!this.configuration.isVertical) {
        boardItems.push(
          this.createItem(
            FencePreviewItemTypeEnum.Board,
            this.fenceService.imgPath(color.path, cut),
            boardItems.length + 1,
            color.height
          )
        );
      }
      // Lames verticales
      else {
        const verticalPath = this.verticalPathArray().find(
          x => x.path === color.path && x.widthRatio === widthRatio
        ).verticalPath;

        boardItems.push(
          this.createItem(FencePreviewItemTypeEnum.Board, verticalPath, boardItems.length + 1, color.width, true)
        );
      }
    }

    //On insère le profilé du bas si on a une image
    if (profile?.pathBottom) {
      const profileVisual = this.fenceService.imgPath(profile.pathBottom, cut);

      if (profileVisual) {
        boardItems.push(this.createItem(FencePreviewItemTypeEnum.BottomProfile, profileVisual, boardItems.length + 1));
      }
    }

    return boardItems;
  }

  private async buildHorizontalBoardsByInstallation(
    installationColor: FencePanelInstallationColors,
    cut: boolean
  ): Promise<FencePreviewItem[]> {
    const boardItems: FencePreviewItem[] = [];

    const color = installationColor.colors[0];

    switch (installationColor.installation.installationType) {
      // Pour les poses emboitées, on insère le profilé de finition sur toutes les poses
      case InstallationTypeEnum.Interlocking:
        // On insère le profilé du haut si on a une image
        if (color?.profile?.pathTop) {
          const profileVisual = this.fenceService.imgPath(color.profile.pathTop, cut);

          if (profileVisual) {
            boardItems.push(this.createItem(FencePreviewItemTypeEnum.TopProfile, profileVisual, boardItems.length + 1));
          }
        }
        break;

      // Pour les poses persiennées, on insère l'entretoise de finition sur toutes les poses
      case InstallationTypeEnum.Shutter:
        if (color.finishingSpacerHeight) {
          boardItems.push(this.createSpacerPreviewItem(color.finishingSpacerHeight, boardItems.length + 1));
        }
        break;
    }

    // on insère les lames
    installationColor.colors.forEach((color, index) => {
      // ajout de l'entretoise si la pose en contient et si ce n'est pas la première lame
      // on affiche les entretoises qu'entre deux lames d'une même pose
      if (index > 0 && color.spacerHeight) {
        boardItems.push(this.createSpacerPreviewItem(color.spacerHeight, boardItems.length + 1));
      }

      boardItems.push(
        this.createItem(
          FencePreviewItemTypeEnum.Board,
          this.fenceService.imgPath(color.path, cut),
          boardItems.length + 1,
          color.height
        )
      );
    });

    switch (installationColor.installation.installationType) {
      // Pour les poses emboitées, on insère le profilé de départ sur toutes les poses
      case InstallationTypeEnum.Interlocking:
        // On insère le profilé du bas si on a une image
        if (color?.profile?.pathBottom) {
          const profileVisual = this.fenceService.imgPath(color.profile.pathBottom, cut);

          if (profileVisual) {
            boardItems.push(
              this.createItem(FencePreviewItemTypeEnum.BottomProfile, profileVisual, boardItems.length + 1)
            );
          }
        }
        break;

      // Pour les poses persiennées, on insère l'entretoise de départ sur toutes les poses
      case InstallationTypeEnum.Shutter:
        if (color.startingSpacerHeight) {
          boardItems.push(this.createSpacerPreviewItem(color.startingSpacerHeight, boardItems.length + 1));
        }
        break;
    }

    return boardItems;
  }

  private createItem(
    type: FencePreviewItemTypeEnum,
    path: string,
    position: number,
    height: number = null,
    isVertical = false
  ): FencePreviewItem {
    return {
      id: Guid.create(),
      product: {
        type: type,
        path: path,
        height: height,
        isVertical: isVertical,
      },
      position: position,
    };
  }

  /**
   * Fonction de récupération du visuel du décor
   * @param decorationType decor ou panneau décor ou lame décorative
   * @param orientation horizontal ou vertical
   * @param position pour indiquer quel élément du tableau on veut récupérer
   * @returns url du visuel ou false
   */
  private async getDecorationPath(
    decorationType: 'decoration' | 'panel' | 'decorative-board',
    orientation: string,
    position: number = 0
  ): Promise<string> {
    let decors: Decoration[] = [];

    switch (decorationType) {
      case 'decoration':
        switch (orientation) {
          case 'horizontal':
            decors = this.configuration.horizontalDecorations;
            break;
          case 'vertical':
            for (const side of this.configuration.sides) {
              if (side.verticalDecorations != undefined) {
                for (const d of side.verticalDecorations.filter(d => d.quantity > 0)) {
                  if (d.verticalPath == null) {
                    d.verticalPath = await rotateImg(this.fenceService.imgPath(d.path), 270);
                  }
                  decors.push(d);
                }
              }
            }
            break;
        }
        break;
      case 'panel':
        switch (orientation) {
          case 'horizontal':
            decors = this.configuration.horizontalPanels;
            break;
          case 'vertical':
            for (const side of this.configuration.sides) {
              if (side.verticalPanels != undefined) {
                for (const d of side.verticalPanels.filter(d => d.quantity > 0)) {
                  if (d.verticalPath == null) {
                    d.verticalPath = await rotateImg(this.fenceService.imgPath(d.path), 270);
                  }
                  decors.push(d);
                }
              }
            }
            break;
        }
        break;
      case 'decorative-board':
        switch (orientation) {
          case 'horizontal':
            decors = this.configuration.horizontalDecorativeBoards;
            break;
          case 'vertical':
            for (const side of this.configuration.sides) {
              if (side.verticalDecorativeBoards != undefined) {
                for (const d of side.verticalDecorativeBoards.filter(d => d.quantity > 0)) {
                  if (d.verticalPath == null) {
                    d.verticalPath = await rotateImg(this.fenceService.imgPath(d.path), 270);
                  }
                  decors.push(d);
                }
              }
            }
            break;
        }
        break;
      default:
        decors = undefined;
    }

    if (decors) {
      switch (orientation) {
        case 'horizontal':
          return decors.find(d => d.quantity > 0)
            ? this.fenceService.imgPath(decors.filter(d => d.quantity > 0)[position].path)
            : undefined;
        case 'vertical':
          return decors.find(d => d.quantity > 0)
            ? decors.filter(d => d.quantity > 0)[position].verticalPath
            : undefined;
      }
    }
    return undefined;
  }

  /**
   * Réinitialisation du service
   */
  reset() {
    this.items.set([]);
    this.differentHeights.set([]);
    this.resumeGroups.set([]);
  }

  /**
   * Créer l'élément de l'entretoise à utiliser dans la preview
   * @param spacerHeight
   * @param position
   * @private
   */
  private createSpacerPreviewItem(spacerHeight: number, position: number): FencePreviewItem {
    return {
      id: Guid.create(),
      product: {
        type: FencePreviewItemTypeEnum.Spacer,
        path: null,
        height: spacerHeight,
      },
      position: position,
    };
  }

  /**
   * Fonction permettant de récupérer la décoration avec la plus grande quantité
   * @param decorations
   * @returns La décoration avec la quantité la plus élevée
   */
  private getDecorationWithMaxQuantity(decorations: Decoration[]): Decoration | undefined {
    if (!decorations?.length) return undefined;

    return decorations.reduce((max, item) => (item.quantity > max.quantity ? item : max));
  }
}
